// Cliente HTTP de la API del catálogo (Req. 18.1, 18.4).
//
// Adjunta el JWT de Cognito (idToken) en `Authorization: Bearer` a cada
// petición, serializa/parsea JSON y aplica REINTENTOS con backoff ante errores
// de red o 5xx (degradación controlada con conexión limitada — Req. 17.4, 18.4).
// Un 401 marca la sesión como inválida (se notifica vía callback).

/** Error de la API con status y cuerpo para que la UI ramifique. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Error de red/conexión (sin respuesta del servidor). */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/** Opciones de una petición. */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** Nº de reintentos ante red/5xx (por defecto 2). */
  retries?: number;
}

/** Cliente configurable con la URL base y el proveedor del token. */
export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: () => string | null,
    private readonly onUnauthorized?: () => void,
  ) {}

  /** Realiza una petición y devuelve el JSON parseado como `T`. */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', query, body, retries = 2 } = options;
    const url = this.buildUrl(path, query);
    const headers: Record<string, string> = { accept: 'application/json' };
    const token = this.getToken();
    if (token) headers['authorization'] = `Bearer ${token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';

    let attempt = 0;
    for (;;) {
      let res: Response;
      try {
        res = await fetch(url, {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
      } catch {
        // Error de red: reintentar con backoff si quedan intentos (Req. 17.4).
        if (attempt < retries) {
          await sleep(backoff(attempt));
          attempt += 1;
          continue;
        }
        throw new NetworkError('No se pudo conectar con el servidor. Revisa tu conexión.');
      }

      if (res.status === 401) {
        this.onUnauthorized?.();
        throw new ApiError(401, 'unauthenticated', 'Sesión expirada o no válida.');
      }

      if (res.status >= 500 && attempt < retries) {
        await sleep(backoff(attempt));
        attempt += 1;
        continue;
      }

      const text = await res.text();
      const json = text ? safeJson(text) : undefined;
      if (!res.ok) {
        const obj = (json ?? {}) as Record<string, unknown>;
        const code = String(obj['error'] ?? `http_${res.status}`);
        const message = String(obj['message'] ?? obj['reason'] ?? `Error ${res.status}`);
        throw new ApiError(res.status, code, message, json);
      }
      return json as T;
    }
  }

  private buildUrl(path: string, query?: RequestOptions['query']): string {
    const base = this.baseUrl.replace(/\/$/, '');
    const url = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function backoff(attempt: number): number {
  return 400 * 2 ** attempt;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
